import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Area,
} from "recharts";
import PropTypes from "prop-types";
import { Logger } from "@base-provider";
import CustomTooltip from "../shared/customTooltip";
import useStyled from "../../../hooks/useStyled";

const { theme, styled } = useStyled();

const StyledLegend = styled("div")(({ theme }) => ({
  fontSize: 10,
  fontWeight: 400,
  maxWidth: 60,
  paddingRight: 16,
  wordBreak: "break-word",
  textAlign: "right",
}));
const StyledChartWrapper = styled("div")(({ theme }) => ({
  paddingLeft: "10px",
}));

const CustomizedLegend = ({ legend }) => {
  return <StyledLegend>{legend}</StyledLegend>;
};

const CustomActiveDot = (props) => {
  const { cx, cy, index, color, onActiveChange } = props;
  const SIZE = 0;

  useEffect(() => {
    let newPoint = {
      x: cx,
      y: cy,
      index: index,
    };
    onActiveChange(newPoint);
  }, [index]);

  return (
    <svg x={cx - SIZE} y={cy - SIZE} fill={color}>
      <circle cx={SIZE} cy={SIZE} r={SIZE} />
    </svg>
  );
};

const SFAreaChart = ({
  data,
  xAxisKeyName = "name",
  lines,
  tooltipBody,
  legend,
  width = 600,
  height = 300,
  yAxis,
  yAxisTickFormatter,
}) => {
  const { t } = useTranslation();
  const [activePoint, setActivePoint] = useState({
    x: 0,
    y: 0,
    index: null,
  });
  const [hoveredIndex, setHoveredIndex] = useState(null);

  Logger.of("App.Components.Shared.Line.ChartContainer").info({
    data,
    xAxisKeyName,
    lines,
    legend,
    yAxis,
  });

  // CustomXAxis
  const CustomXAxis = (props) => {
    const { x, y, payload, index } = props;

    return (
      <g transform={`translate(${x},${y - 21})`}>
        {/* dot for each tick */}
        <circle
          r={4}
          fill={index === hoveredIndex ? "#fff" : "#888"}
          opacity={index === hoveredIndex ? 1 : 0.5}
        />
        {/* only draw line if index matches */}
        {index === hoveredIndex && <line y2={-140} />}
        <text dy={20} textAnchor="middle" fill="#fff">
          {payload.value}
        </text>
      </g>
    );
  };

  const TooltipContent = ({ payload, label, active }) => {
    if (active && payload && payload.length) {
      const body = tooltipBody(label, payload);
      return <CustomTooltip>{body}</CustomTooltip>;
    }

    return null;
  };
  return (
    <StyledChartWrapper>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          width={width}
          height={height}
          data={data}
          margin={{
            top: 20,
            right: 10,
            left: 5,
            bottom: 5,
          }}
          onMouseMove={(e) => setHoveredIndex(e?.activeTooltipIndex)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <defs>
            <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="50%" stopColor="#fff" stopOpacity={0.25} />
              <stop offset="99%" stopColor="#fff" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid
            vertical={false}
            stroke="#ccc"
            strokeDasharray="5 6"
            strokeOpacity={0.1}
          />
          <XAxis
            type="category"
            dataKey={xAxisKeyName}
            fontSize={12}
            fontWeight={600}
            lineHeight={12}
            tickLine={false}
            tickMargin={15}
            interval={0}
            minTickGap={50}
            padding={{ right: 30 }}
            stroke="#fff"
            tick={<CustomXAxis />}
            axisLine={false}
          />
          {yAxis && (
            <YAxis
              axisLine={false}
              type="number"
              minTickGap={5}
              fontSize={10}
              fontWeight={400}
              tickCount={yAxis.ticks.length}
              tickLine={false}
              tickMargin={15}
              lineHeight={12}
              domain={["dataMin", "dataMax"]}
              ticks={yAxis.ticks}
              tick={{ fill: "#fff" }}
              tickFormatter={yAxisTickFormatter}
              {...(yAxis.tickAppendix && {
                tickFormatter: (val) =>
                  t("components.chart.appendix", {
                    value: val,
                    appendix: yAxis.tickAppendix,
                  }),
              })}
            />
          )}
          {tooltipBody && (
            <Tooltip
              cursor={{
                stroke: theme.palette.grey50.main,
                strokeWidth: 1,
                strokeDasharray: "2 3",
              }}
              content={<TooltipContent />}
              position={{ x: activePoint.x, y: 20 }}
            />
          )}
          {legend && yAxis && (
            <Legend
              align="left"
              verticalAlign="bottom"
              wrapperStyle={{ bottom: 25 }}
              content={<CustomizedLegend legend={legend} />}
            />
          )}
          {lines.map((l, index) => (
            <Area
              key={index}
              type={l.type ?? "linear"}
              dataKey={l.dataKey}
              stroke={l.stroke ?? "#fff"}
              fill="url(#area)"
              strokeWidth={l.strokeWidth ?? 1}
              activeDot={
                <CustomActiveDot
                  color={l.dotStroke}
                  onActiveChange={setActivePoint}
                />
              }
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartWrapper>
  );
};

SFAreaChart.propTypes = {
  data: PropTypes.array,
  xAxisKeyName: PropTypes.string,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf([
        "basis",
        "basisClosed",
        "basisOpen",
        "linear",
        "linearClosed",
        "natural",
        "monotoneX",
        "monotoneY",
        "monotone",
        "step",
        "stepBefore",
        "stepAfter",
        PropTypes.func,
      ]),
      dataKey: PropTypes.string,
      stroke: PropTypes.string,
      dotStroke: PropTypes.string,
      strokeWidth: PropTypes.number,
    })
  ),
  yAxis: PropTypes.shape({
    ticks: PropTypes.array,
    tickAppendix: PropTypes.string,
  }),
  legend: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  tooltipBody: PropTypes.any,
  yAxisTickFormatter: PropTypes.func,
};

export default SFAreaChart;
